import type { ComponentProps, ComponentType } from 'react'
import { Suspense } from 'react'
import { Skeleton } from '@/components/ui/skeleton'

type MonacoEditorProps = ComponentProps<
  typeof import('@monaco-editor/react')['default']
>

interface MonacoLoaderProps {
  MonacoComponent: ComponentType<MonacoEditorProps>
  isClient: boolean
  monacoProps: MonacoEditorProps
  fallback?: React.ReactNode
  preparing?: React.ReactNode
}

function CodeEditorSkeleton() {
  return (
    <div className="h-full w-full space-y-2 p-4">
      {/* Line numbers area */}
      <div className="flex gap-4">
        <div className="flex w-8 flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton elements
            <Skeleton key={`line-${i}`} className="h-4 w-6" />
          ))}
        </div>
        {/* Code lines */}
        <div className="flex flex-1 flex-col gap-2">
          {[
            { width: 'w-32' },
            { width: 'w-40' },
            { width: 'w-24', indent: 'ml-4' },
            { width: 'w-36', indent: 'ml-4' },
          ].map((line, i) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton elements
              key={`code-${i}`}
              className={`h-4 ${line.width} ${line.indent || ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function MonacoLoader({
  MonacoComponent,
  isClient,
  monacoProps,
  fallback,
  preparing,
}: MonacoLoaderProps) {
  const fallbackNode = fallback ?? <CodeEditorSkeleton />

  const preparingNode = preparing ?? <CodeEditorSkeleton />

  return (
    <Suspense fallback={fallbackNode}>
      {isClient ? <MonacoComponent {...monacoProps} /> : preparingNode}
    </Suspense>
  )
}
